import React, { useContext } from 'react';
import * as PortalApi from '../../lib/portal-api';
import * as gtag from '../../lib/gtag';

export interface SwaggerContextState {
  isAuthenticated: boolean;
  projectEnvironment: string;
  accessToken: string;
  availableOperations: string[];
  credentialType: PortalApi.CredentialType;
  credentialFlow: PortalApi.AuthFlow;
  projectId: string;
  credentialId: string;
  authenticate: (
    projectEnvironment: PortalApi.ProjectEnvironment,
    accessToken: string,
    credentialType: PortalApi.CredentialType,
    credentialFlow: PortalApi.AuthFlow,
    credentialId: string,
  ) => void;
  reset: () => void;
  setProjectId: (projectId: string) => void;
  setAvailableOperations: (ops: string[]) => void;
}

export const DefaultSwaggerContext: SwaggerContextState = {
  isAuthenticated: false,
  accessToken: '',
  availableOperations: [],
  projectEnvironment: 'primary',
  credentialType: 'apiKey',
  credentialFlow: 'clientCredentials',
  projectId: '',
  credentialId: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  reset: () => {},
  authenticate: (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    projectEnvironment: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    accessToken: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    credentialType: PortalApi.CredentialType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    credentialFlow: PortalApi.AuthFlow,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    credentialId: string,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
  ) => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  setProjectId: (projectId: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  setAvailableOperations: (ops: string[]) => {},
};

export const SwaggerContext = React.createContext<SwaggerContextState>(
  DefaultSwaggerContext,
);

export const useSwaggerContext = (): SwaggerContextState =>
  useContext(SwaggerContext);

// Store the currently authenticated swagger details to allow it to persist on multiple documentation pages
export class SwaggerContextProvider extends React.Component<
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  SwaggerContextState
> {
  state = DefaultSwaggerContext;

  setProjectId = (projectId: string): void => {
    this.setState({
      projectId,
    });
  };

  setAvailableOperations = (availableOperations: string[]): void => {
    this.setState({
      availableOperations,
    });
  };

  authenticate = (
    projectEnvironment: string,
    accessToken: string,
    credentialType: PortalApi.CredentialType,
    credentialFlow: PortalApi.AuthFlow,
    credentialId: string,
  ): void => {
    this.setState({
      accessToken,
      projectEnvironment,
      credentialType,
      isAuthenticated: accessToken !== '',
      credentialFlow,
      credentialId,
    });

    gtag.event({
      category: 'docs-live-ui',
      action: 'get-token-response-received',
      label: accessToken === '' ? 'invalid-token' : 'valid-token',
    });
  };

  reset = (): void => {
    this.setState({
      accessToken: '',
      availableOperations: [],
      credentialType: 'apiKey',
      credentialFlow: 'clientCredentials',
      credentialId: '',
      isAuthenticated: false,
    });
  };

  render(): React.ReactNode {
    return (
      <SwaggerContext.Provider
        value={{
          isAuthenticated: this.state.isAuthenticated,
          accessToken: this.state.accessToken,
          credentialType: this.state.credentialType,
          credentialFlow: this.state.credentialFlow,
          credentialId: this.state.credentialId,
          projectId: this.state.projectId,
          availableOperations: this.state.availableOperations,
          projectEnvironment: this.state.projectEnvironment,
          reset: () => this.reset(),
          setProjectId: (projectId: string) => this.setProjectId(projectId),
          setAvailableOperations: (availableOperations: string[]) =>
            this.setAvailableOperations(availableOperations),
          authenticate: (
            projectEnvironment: PortalApi.ProjectEnvironment,
            accessToken: string,
            credentialType: PortalApi.CredentialType,
            credentialFlow: PortalApi.AuthFlow,
            credentialId: string,
          ) =>
            this.authenticate(
              projectEnvironment,
              accessToken,
              credentialType,
              credentialFlow,
              credentialId,
            ),
        }}
      >
        {this.props.children}
      </SwaggerContext.Provider>
    );
  }
}
